import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Edit, Save, X, 
  Filter, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adminInputClass =
  "w-full bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectTriggerClass =
  "bg-misel-card border border-white/15 text-white rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed data-[placeholder]:text-gray-500";
const adminSelectContentClass =
  "z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl";
const adminSelectItemClass =
  "text-white focus:bg-primary/20 focus:text-white";

const RULE_FIELDS = [
  { value: 'subscription_status', label: 'Κατάσταση Συνδρομής', labelEn: 'Subscription Status' },
  { value: 'has_phone', label: 'Έχει Τηλέφωνο', labelEn: 'Has Phone' },
  { value: 'email_consent', label: 'Email Συναίνεση', labelEn: 'Email Consent' },
  { value: 'sms_consent', label: 'SMS Συναίνεση', labelEn: 'SMS Consent' },
  { value: 'role', label: 'Ρόλος', labelEn: 'Role' },
  { value: 'created_days_ago', label: 'Ημέρες από Εγγραφή', labelEn: 'Days Since Registration' },
];

const OPERATORS = [
  { value: 'equals', label: 'Ίσο με', labelEn: 'Equals' },
  { value: 'not_equals', label: 'Διαφορετικό από', labelEn: 'Not Equals' },
  { value: 'greater_than', label: 'Μεγαλύτερο από', labelEn: 'Greater Than' },
  { value: 'less_than', label: 'Μικρότερο από', labelEn: 'Less Than' },
];

export default function SegmentsManagement() {
  const { i18n } = useTranslation();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSegment, setEditingSegment] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const isEl = i18n.language === 'el';

  const emptySegment = {
    name: '',
    name_el: '',
    description: '',
    rules: [{ field: 'subscription_status', operator: 'equals', value: 'active' }],
    is_active: true
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/messaging/segments`, { headers });
      setSegments(res.data);
    } catch (e) {
      toast.error(isEl ? 'Σφάλμα φόρτωσης' : 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingSegment.name) {
      toast.error(isEl ? 'Εισάγετε όνομα' : 'Enter name');
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        name: editingSegment.name,
        name_el: editingSegment.name_el || editingSegment.name,
        description: editingSegment.description || '',
        rules: editingSegment.rules,
        is_active: editingSegment.is_active
      };

      if (editingSegment.id) {
        await axios.put(`${API}/admin/messaging/segments/${editingSegment.id}`, data, { headers });
        toast.success(isEl ? 'Ενημερώθηκε' : 'Updated');
      } else {
        await axios.post(`${API}/admin/messaging/segments`, data, { headers });
        toast.success(isEl ? 'Δημιουργήθηκε' : 'Created');
      }
      
      setShowForm(false);
      setEditingSegment(null);
      fetchSegments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (segmentId) => {
    if (!confirm(isEl ? 'Διαγραφή segment;' : 'Delete segment?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/messaging/segments/${segmentId}`, { headers });
      toast.success(isEl ? 'Διαγράφηκε' : 'Deleted');
      fetchSegments();
    } catch (e) {
      toast.error('Error');
    }
  };

  const addRule = () => {
    setEditingSegment(prev => ({
      ...prev,
      rules: [...prev.rules, { field: 'subscription_status', operator: 'equals', value: 'active' }]
    }));
  };

  const updateRule = (index, key, value) => {
    setEditingSegment(prev => ({
      ...prev,
      rules: prev.rules.map((r, i) => i === index ? { ...r, [key]: value } : r)
    }));
  };

  const removeRule = (index) => {
    setEditingSegment(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const getRuleValueInput = (rule, index) => {
    if (rule.field === 'subscription_status') {
      return (
        <Select value={rule.value} onValueChange={(value) => updateRule(index, 'value', value)}>
          <SelectTrigger className={adminSelectTriggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={adminSelectContentClass}>
            <SelectItem className={adminSelectItemClass} value="active">{isEl ? 'Ενεργή' : 'Active'}</SelectItem>
            <SelectItem className={adminSelectItemClass} value="inactive">{isEl ? 'Ανενεργή' : 'Inactive'}</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (rule.field === 'has_phone' || rule.field === 'email_consent' || rule.field === 'sms_consent') {
      return (
        <Select value={rule.value?.toString()} onValueChange={(value) => updateRule(index, 'value', value === 'true')}>
          <SelectTrigger className={adminSelectTriggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={adminSelectContentClass}>
            <SelectItem className={adminSelectItemClass} value="true">{isEl ? 'Ναι' : 'Yes'}</SelectItem>
            <SelectItem className={adminSelectItemClass} value="false">{isEl ? 'Όχι' : 'No'}</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (rule.field === 'role') {
      return (
        <Select value={rule.value} onValueChange={(value) => updateRule(index, 'value', value)}>
          <SelectTrigger className={adminSelectTriggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={adminSelectContentClass}>
            <SelectItem className={adminSelectItemClass} value="user">User</SelectItem>
            <SelectItem className={adminSelectItemClass} value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        type={rule.field === 'created_days_ago' ? 'number' : 'text'}
        value={rule.value}
        onChange={(e) => updateRule(index, 'value', e.target.value)}
        className={adminInputClass}
        placeholder={isEl ? 'Τιμή...' : 'Value...'}
      />
    );
  };

  return (
    <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Users className="text-primary" size={28} />
              <h1 className="text-3xl font-bold text-white">
                {isEl ? 'Segments Χρηστών' : 'User Segments'}
              </h1>
            </div>
            <Button
              onClick={() => {
                setEditingSegment({ ...emptySegment });
                setShowForm(true);
              }}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus size={16} className="mr-2" />
              {isEl ? 'Νέο Segment' : 'New Segment'}
            </Button>
          </div>

          {/* Segment Form */}
          {showForm && editingSegment && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">
                {editingSegment.id 
                  ? (isEl ? 'Επεξεργασία Segment' : 'Edit Segment')
                  : (isEl ? 'Νέο Segment' : 'New Segment')
                }
              </h2>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    {isEl ? 'Όνομα (EN)' : 'Name (EN)'}
                  </label>
                  <Input
                    value={editingSegment.name}
                    onChange={(e) => setEditingSegment(prev => ({ ...prev, name: e.target.value }))}
                    className={adminInputClass}
                    placeholder="Active Subscribers"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    {isEl ? 'Όνομα (EL)' : 'Name (EL)'}
                  </label>
                  <Input
                    value={editingSegment.name_el}
                    onChange={(e) => setEditingSegment(prev => ({ ...prev, name_el: e.target.value }))}
                    className={adminInputClass}
                    placeholder="Ενεργοί Συνδρομητές"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-1 block">
                  {isEl ? 'Περιγραφή' : 'Description'}
                </label>
                <Input
                  value={editingSegment.description}
                  onChange={(e) => setEditingSegment(prev => ({ ...prev, description: e.target.value }))}
                  className={adminInputClass}
                />
              </div>

              {/* Rules */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-400 flex items-center gap-2">
                    <Filter size={14} />
                    {isEl ? 'Κανόνες' : 'Rules'}
                  </label>
                  <Button variant="ghost" size="sm" onClick={addRule}>
                    <Plus size={14} className="mr-1" />
                    {isEl ? 'Προσθήκη' : 'Add'}
                  </Button>
                </div>

                <div className="space-y-2">
                  {editingSegment.rules.map((rule, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/5 p-3 rounded">
                      <Select value={rule.field} onValueChange={(value) => updateRule(index, 'field', value)}>
                        <SelectTrigger className={adminSelectTriggerClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={adminSelectContentClass}>
                          {RULE_FIELDS.map(f => (
                            <SelectItem className={adminSelectItemClass} key={f.value} value={f.value}>
                              {isEl ? f.label : f.labelEn}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select value={rule.operator} onValueChange={(value) => updateRule(index, 'operator', value)}>
                        <SelectTrigger className={adminSelectTriggerClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={adminSelectContentClass}>
                          {OPERATORS.map(o => (
                            <SelectItem className={adminSelectItemClass} key={o.value} value={o.value}>
                              {isEl ? o.label : o.labelEn}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {getRuleValueInput(rule, index)}
                      
                      {editingSegment.rules.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRule(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                  {isEl ? 'Αποθήκευση' : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSegment(null);
                  }}
                  className="border-white/20"
                >
                  {isEl ? 'Ακύρωση' : 'Cancel'}
                </Button>
              </div>
            </div>
          )}

          {/* Segments List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          ) : segments.length === 0 ? (
            <div className="text-center py-16 bg-misel-card border border-white/10 rounded-sm">
              <Users className="mx-auto mb-4 text-gray-500" size={48} />
              <h2 className="text-xl text-white mb-2">
                {isEl ? 'Δεν υπάρχουν segments' : 'No segments yet'}
              </h2>
              <p className="text-gray-400">
                {isEl ? 'Δημιουργήστε το πρώτο σας segment' : 'Create your first segment'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {segments.map((segment) => (
                <div
                  key={segment.id}
                  className="bg-misel-card border border-white/10 rounded-sm p-5"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {isEl ? segment.name_el || segment.name : segment.name}
                      </h3>
                      {segment.description && (
                        <p className="text-sm text-gray-400 mt-1">{segment.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <span className="flex items-center gap-1 text-sm text-primary">
                          <Users size={14} />
                          {segment.user_count} {isEl ? 'χρήστες' : 'users'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          segment.is_active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {segment.is_active ? (isEl ? 'Ενεργό' : 'Active') : (isEl ? 'Ανενεργό' : 'Inactive')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingSegment(segment);
                          setShowForm(true);
                        }}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(segment.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Rules preview */}
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-1">{isEl ? 'Κανόνες:' : 'Rules:'}</p>
                    <div className="flex flex-wrap gap-2">
                      {segment.rules?.map((rule, i) => (
                        <span key={i} className="px-2 py-1 bg-white/5 rounded text-xs text-gray-300">
                          {rule.field} {rule.operator} {String(rule.value)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}
